import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useErrors } from "@auth0/auth0-acul-react/signup-password";
import type { ErrorItem } from "@auth0/auth0-acul-react/types";

import Captcha from "@/components/Captcha/index";
import { ULThemeAlert, ULThemeAlertTitle } from "@/components/ULThemeError";
import { useCaptcha } from "@/hooks/useCaptcha";

import { useSignupPasswordManager } from "../hooks/useSignupPasswordManager";
import CheckDetailsStep from "./wizard/CheckDetailsStep";
import PersonalInfoStep from "./wizard/PersonalInfoStep";
import SetPasswordStep from "./wizard/SetPasswordStep";
import { WIZARD_DEFAULT_VALUES, type WizardFormValues } from "./wizard/types";

// Every Action-side validation error (api.validation.error(...) with no
// `field` argument, per the existing convention documented in
// ARCHITECTURE.md) is about a Personal-info field, so on any general error
// after a Join attempt, navigate back to sub-step 1 where the offending
// field lives — see brief item 4 under "Verification (live)".
const SUB_STEP_ON_SERVER_ERROR = 1;

interface SignupPasswordFormProps {
  subStep: 1 | 2 | 3;
  onSubStepChange: (step: 1 | 2 | 3) => void;
}

function SignupPasswordForm({ subStep, onSubStepChange }: SignupPasswordFormProps) {
  const { handleSignupPassword, screenData, flow, isCaptchaAvailable, captcha, locales } =
    useSignupPasswordManager();
  const { errors, hasError, dismiss } = useErrors();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set when the direct-stash POST fails (API down, rate-limited, …). The
  // Auth0 signup was NOT submitted in that case, so this is a local error,
  // not one from useErrors(). See useSignupPasswordManager.
  const [stashError, setStashError] = useState(false);

  const form = useForm<WizardFormValues>({
    defaultValues: WIZARD_DEFAULT_VALUES,
    reValidateMode: "onBlur",
  });

  const generalErrors: ErrorItem[] = errors.byType("auth0").filter((err) => !err.field);

  // Bot detection on this screen.
  //
  // The token does NOT travel through react-hook-form. Only the legacy `auth0`
  // image captcha registers a field; the widget providers (auth0_v2, hcaptcha,
  // recaptcha) report their token solely through onCaptchaResponse ->
  // useCaptcha. Reading `captchaValue` here is therefore the only way it
  // reaches signup() — dropping it sends no captcha at all and Auth0 rejects
  // with `invalid-recaptcha`, re-rendering the screen from scratch. That is
  // exactly the silent loop that hit signup-id; see ARCHITECTURE.md "Captcha".
  const { captchaConfig, captchaProps, captchaValue, isCaptchaSolved } = useCaptcha(
    captcha || undefined,
    `${locales.form.fields.captcha.label}*`
  );
  const captchaRequired = isCaptchaAvailable && !!captchaConfig;
  const captchaSDKError = errors.byField("captcha")[0]?.message;

  // Rendered by whichever sub-step is terminal for this flow — never earlier.
  // Turnstile tokens expire, so issuing one on Personal info and submitting
  // several minutes later at Join would fail validation on a challenge the
  // member solved correctly.
  const captchaSlot = captchaRequired ? (
    <div className="mb-4">
      <Captcha
        control={form.control}
        name="captcha"
        captcha={captchaConfig!}
        {...captchaProps}
        sdkError={captchaSDKError}
      />
    </div>
  ) : null;

  // A server-side rejection on Join surfaces here (the SDK re-renders with
  // the Action's error in the screen context) — jump back to sub-step 1 so
  // the alert is visible next to the field that likely caused it.
  useEffect(() => {
    if (hasError && generalErrors.length > 0) {
      setIsSubmitting(false);
      onSubStepChange(SUB_STEP_ON_SERVER_ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, generalErrors.length]);

  const handleJoin = async () => {
    // Flow A gates Join on the step-5 declaration checkbox. Flow B has no
    // review step and no declaration here — it is collected by the
    // progressive-profiling form, which complete-profile requires before a
    // member can reach COMPLETE.
    if (flow !== "B") {
      const valid = await form.trigger("consentAccepted");
      if (!valid) return;
    }

    // Belt-and-braces: the Join button is already disabled until the challenge
    // resolves, but a token can expire between resolving and clicking.
    if (captchaRequired && !isCaptchaSolved) return;

    setIsSubmitting(true);
    const values = form.getValues();
    // captchaValue (widget providers) wins; values.captcha covers the legacy
    // image captcha, which is the only provider that populates the field.
    const captchaToken = captchaValue ?? values.captcha;

    if (flow === "B") {
      await handleSignupPassword({
        flow,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        mobile: values.mobile,
        captcha: captchaToken,
      });
      return;
    }

    setStashError(false);
    const result = await handleSignupPassword({
      flow,
      password: values.password,
      captcha: captchaToken,
      gender: values.gender,
      title: values.title,
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
      mobile: values.mobile,
      country: values.country,
      residentialAddress: values.residentialAddress,
      postalAddress: values.postalSameAsResidential ? undefined : values.postalAddress,
      tfn: values.tfn || undefined,
      consentAccepted: values.consentAccepted,
      declarationAcceptedAt: new Date().toISOString(),
    });
    if (result.status === "stash_failed") {
      // Nothing reached Auth0. Stay on the review step so the member can
      // simply press Join again once the problem clears.
      setStashError(true);
      setIsSubmitting(false);
      return;
    }
    // isSubmitting intentionally left true on success — the page is about to
    // navigate away (MFA enrollment); only the error path above resets it.
  };

  return (
    <FormProvider {...form}>
      {stashError && (
        <div className="space-y-3 mb-4" data-testid="stash-error">
          <ULThemeAlert variant="destructive" onDismiss={() => setStashError(false)}>
            <ULThemeAlertTitle>{locales.form.stashError}</ULThemeAlertTitle>
          </ULThemeAlert>
        </div>
      )}

      {hasError && generalErrors.length > 0 && (
        <div className="space-y-3 mb-4">
          {generalErrors.map((error) => (
            <ULThemeAlert key={error.id} variant="destructive" onDismiss={() => dismiss(error.id)}>
              <ULThemeAlertTitle>{error.message}</ULThemeAlertTitle>
            </ULThemeAlert>
          ))}
        </div>
      )}

      {subStep === 1 && (
        <PersonalInfoStep onContinue={() => onSubStepChange(2)} flow={flow} />
      )}
      {/* Flow B ends here: Set password is the last sub-step, so its primary
          button submits rather than advancing to a review step. */}
      {subStep === 2 && (
        <SetPasswordStep
          onContinue={flow === "B" ? handleJoin : () => onSubStepChange(3)}
          onBack={() => onSubStepChange(1)}
          isSubmit={flow === "B"}
          isSubmitting={flow === "B" ? isSubmitting : false}
          // Flow A's terminal step is Check details, so it must not render the
          // challenge here — it would expire before Join.
          captchaSlot={flow === "B" ? captchaSlot : null}
          blockPrimary={flow === "B" && captchaRequired && !isCaptchaSolved}
        />
      )}
      {subStep === 3 && (
        <CheckDetailsStep
          onEdit={() => onSubStepChange(1)}
          onBack={() => onSubStepChange(2)}
          onJoin={handleJoin}
          isSubmitting={isSubmitting}
          email={screenData?.email}
          captchaSlot={captchaSlot}
          blockPrimary={captchaRequired && !isCaptchaSolved}
        />
      )}
    </FormProvider>
  );
}

export default SignupPasswordForm;
